import { Plus, ShoppingBag } from "lucide-react";
import { Button } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { OffPremiseGridEntry } from "@/lib/api/types";
import type { DeliveryTabGridEntry } from "@/lib/api/dispatchTypes";
import { deliveryTabBadge, offPremiseTabAvailable, waitingRows } from "@/modules/pos/lib/deliveryWaiting";
import type { useFloorDispatch } from "@/modules/pos/components/dispatch/useFloorDispatch";
import type { useOffPremiseStart } from "@/modules/pos/components/OffPremiseFloor";
import { DeliveryTab } from "./DeliveryTab";

type FloorDispatch = ReturnType<typeof useFloorDispatch>;
type OffPremiseStart = ReturnType<typeof useOffPremiseStart>;

/**
 * «التوصيل والتيك أواي» on a PAPER floor — the wiring both floors share
 * (2026-10-20, user: the till's `/pos` and the manager's live floor
 * `/manage/floor` must be identical for delivery and takeaway). The till's
 * `TableGridPage` and the back office's `FloorLiveView` each own their reads
 * (`useOffPremiseBills`, `useFloorDispatch`, `useOffPremiseStart`) and hand
 * them in here, so the waiting rows, the tab's count and the tab body are
 * computed once, the same way, on both.
 *
 * The rule for whether the tab exists is `offPremiseTabAvailable`: a session
 * that may close a bill (`INVOICE_CLOSE`) or read the dispatch board
 * (`DISPATCH_MANAGE` / `DISPATCH_CASH_CONFIRM`) — not a waiter (2026-10-28).
 * `useDeliveryTabBadge` applies the same rule for a page that has not read
 * the board itself.
 */
export function useFloorDeliveryTab({
  enabled,
  canClose,
  bills,
  floorDispatch,
  now,
}: {
  /** The page may draw the tab at all (`POS_USE`, and PAPER on the till). */
  enabled: boolean;
  /** `INVOICE_CLOSE` — the till's money line, not a waiter's. */
  canClose: boolean;
  bills: readonly OffPremiseGridEntry[];
  floorDispatch: FloorDispatch;
  now: number;
}) {
  const available =
    enabled && offPremiseTabAvailable({ canClose, canSeeBoard: floorDispatch.access.canSeeBoard });
  /* D-37: the waiting bags, joined from the grid and the board on the client. */
  const deliveryBills = bills as DeliveryTabGridEntry[];
  // Only from a board that answered (G24-M3): while it loads or after it
  // failed, a bag already with a driver would read "Assign".
  const rows =
    floorDispatch.access.canSeeBoard && floorDispatch.dispatch.board
      ? waitingRows(deliveryBills, floorDispatch.dispatch.lookup, now)
      : [];
  const badge = deliveryTabBadge(deliveryBills, floorDispatch.access.canSeeBoard, rows);
  return { available, bills: deliveryBills, rows, badge };
}

/**
 * D-35 — the tab's new-order commands at the end of the tab bar, for a session
 * that may open a bill. P24b (the design's `onNewDelivery` / `onNewPickup`):
 * straight into the register — the bill opens at once and the panel asks for
 * the contact. D154: the crew's meal stays one tap away on the comp rung.
 * Returns `undefined` when there is nothing to draw, so the tab bar stays the
 * bare tab group.
 */
export function deliveryTabCommands({
  canOpen,
  canComp,
  start,
  t,
}: {
  canOpen: boolean;
  canComp: boolean;
  start: OffPremiseStart;
  t: ReturnType<typeof useTranslate>;
}) {
  if (!canOpen) return undefined;
  return (
    <>
      <Button
        type="button"
        variant="primary"
        size="md"
        data-testid="start-delivery"
        disabled={start.isStarting}
        leadingIcon={<Plus aria-hidden="true" size={15} strokeWidth={2} />}
        onClick={() => start.beginDirect("DELIVERY")}
      >
        {t((m) => m.dispatch.newDeliveryOrder)}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="md"
        data-testid="start-takeaway"
        disabled={start.isStarting}
        leadingIcon={<ShoppingBag aria-hidden="true" size={15} strokeWidth={1.75} />}
        onClick={() => start.beginDirect("TAKEAWAY")}
      >
        {t((m) => m.dispatch.newPickupOrder)}
      </Button>
      {canComp ? (
        <Button
          type="button"
          variant="ghost"
          size="md"
          data-testid="start-staff-meal"
          disabled={start.isStarting}
          onClick={start.beginStaffMeal}
        >
          {t((m) => m.pos.staffMealStart)}
        </Button>
      ) : null}
    </>
  );
}

/** The tab's body: `DeliveryTab` over the page's dispatch layer. */
export function FloorDeliveryTab({
  delivery,
  floorDispatch,
  now,
  canOpen,
}: {
  delivery: ReturnType<typeof useFloorDeliveryTab>;
  floorDispatch: FloorDispatch;
  now: number;
  canOpen: boolean;
}) {
  return (
    <DeliveryTab
      bills={delivery.bills}
      rows={delivery.rows}
      showWaiting={floorDispatch.access.canSeeBoard}
      waitingLoading={floorDispatch.dispatch.isLoading}
      waitingError={floorDispatch.dispatch.board ? null : floorDispatch.dispatch.error}
      canManage={floorDispatch.access.canManage}
      lookup={floorDispatch.dispatch.lookup}
      board={floorDispatch.dispatch.board}
      dispatchNow={floorDispatch.dispatch.now}
      skewMs={floorDispatch.dispatch.skewMs}
      now={now}
      canOpen={canOpen}
      dispatchChip={floorDispatch.chip}
      dispatchAction={(bill) => floorDispatch.action(bill, "paper", { handOver: true })}
    />
  );
}
